import React, { createContext, useCallback, useRef, useState } from 'react';
import moment from 'moment';
import Filter from '@/pages/dashboard/components/NewFilter';
import { getDateDefault } from '@/pages/dashboard/utils/dateUtils';
import { formatDateFromFilterValue } from '@/pages/dashboard/utils/formatDate';
import { setBtnPointLog } from '@/pages/dashboard/utils/utils';
import { FORM_MAP } from '@/pages/dashboard/utils/_config/constant';

export const CountryRegionOptionsContext = createContext<any[]>([]);

const COUNTRY_SELECTOR = [
  {
    key: 'country',
    visible: 1,
    view_type: 'single',
    default: -1,
    name_cn: '国家',
    name_en: '国家',
  },
];

const getTimeZoneNow = (timeZone: number) =>
  moment(moment().utcOffset(timeZone).format('YYYY-MM-DD HH:mm:ss'));

const lastDays = (days: number, timeZone: number) => {
  const end = getTimeZoneNow(timeZone);
  return [end.clone().subtract(days - 1, 'day'), end];
};

const getPerformanceDateShortcuts = (timeZone: number) => [
  {
    name: 'Today',
    nameCN: '今天',
    type: 'today',
    onClick: () => {
      const now = getTimeZoneNow(timeZone);
      return [now.clone(), now.clone()];
    },
  },
  {
    name: 'Yesterday',
    nameCN: '昨天',
    type: 'yesterday',
    onClick: () => {
      const yesterday = getTimeZoneNow(timeZone).subtract(1, 'day');
      return [yesterday.clone(), yesterday.clone()];
    },
  },
  {
    name: 'This Week',
    nameCN: '本周',
    type: 'thisWeek',
    onClick: () => {
      const now = getTimeZoneNow(timeZone);
      return [now.clone().startOf('isoWeek'), now];
    },
  },
  {
    name: 'Last Week',
    nameCN: '上周',
    type: 'lastWeek',
    onClick: () => {
      const lastWeek = getTimeZoneNow(timeZone).subtract(1, 'week');
      return [lastWeek.clone().startOf('isoWeek'), lastWeek.endOf('isoWeek')];
    },
  },
  {
    name: 'Last 7 Days',
    nameCN: '最近7天',
    type: 'last7Days',
    onClick: () => lastDays(7, timeZone),
  },
  {
    name: 'Last 14 Days',
    nameCN: '最近14天',
    type: 'last14Days',
    onClick: () => lastDays(14, timeZone),
  },
  {
    name: 'Last 30 Days',
    nameCN: '最近30天',
    type: 'last30Days',
    onClick: () => lastDays(30, timeZone),
  },
  {
    name: 'Last 90 Days',
    nameCN: '最近90天',
    type: 'last90Days',
    onClick: () => lastDays(90, timeZone),
  },
  {
    name: 'Last 180 Days',
    nameCN: '最近180天',
    type: 'last180Days',
    onClick: () => lastDays(180, timeZone),
  },
];

const PerformanceFilter = (props: any) => {
  const {
    filterConfirm,
    filterReset,
    filterInit,
    setFilterLoading,
    buttonLogConfig,
    openSticky = false,
    path = 'performance/overall',
    children,
    className,
    config,
    timeZone = 0,
  } = props;
  const gameTimeZone = Number.isFinite(Number(timeZone))
    ? Number(timeZone)
    : 0;
  const filterValueRef = useRef<any>(null);
  const dateDefault = getDateDefault({
    timeType: 'day',
    timeZone: gameTimeZone,
    startNum: 0,
    endNum: 0,
  });
  const { countryRegionOptions = [] } = config || {};
  const [inputList] = useState([
    {
      type: 'rangeDaily',
      key: 'rangeTime',
      default: dateDefault,
      hideEventTab: true,
      hideLatestData: true,
      customShortcuts: getPerformanceDateShortcuts(gameTimeZone),
      overlayClassName:
        'dbd-range-picker-overlay dbd-performance-range-picker-overlay',
    },
    {
      key: 'country',
      options: countryRegionOptions,
      view_type: 'single',
      visible: 1,
      default: -1,
      name_cn: '国家',
      name_en: '国家',
      valueKey: 'country_code',
      cnKey: 'country_name_ch',
      enKey: 'country_name_en',
    },
  ]);

  // Filter UI 需要 Moment；接口层才格式化成字符串
  const toRequestValue = useCallback(
    (value: any, isReset = false) => ({
      ...(formatDateFromFilterValue(value) || {}),
      rangeDateKey: 'rangeDaily',
      timeType: 'Day',
      timeZone: gameTimeZone,
      [FORM_MAP.RANGE_TIME_DIFF]: isReset,
      isReset,
    }),
    [gameTimeZone],
  );

  const onInit = useCallback(
    (value: any, _inputList: any, isHaveExtendFilter: boolean) => {
      filterValueRef.current = value;
      filterInit({
        ...toRequestValue(value),
        [FORM_MAP.RANGE_TIME_DIFF]: !isHaveExtendFilter,
        isReset: false,
      });
      setFilterLoading?.(false);
    },
    [filterInit, setFilterLoading, toRequestValue],
  );

  const onConfirm = useCallback(
    (value: any) => {
      filterValueRef.current = value;
      const normalized = {
        ...toRequestValue(value),
        [FORM_MAP.RANGE_TIME_DIFF]: false,
        isReset: false,
      };
      filterConfirm(normalized);
      setBtnPointLog(buttonLogConfig, normalized);
    },
    [buttonLogConfig, filterConfirm, toRequestValue],
  );

  const onReset = useCallback(
    (value: any) => {
      filterValueRef.current = value;
      const normalized = toRequestValue(value, true);
      filterReset(normalized);
      setBtnPointLog(buttonLogConfig, normalized, true);
    },
    [buttonLogConfig, filterReset, toRequestValue],
  );

  return (
    <CountryRegionOptionsContext.Provider value={countryRegionOptions}>
      <Filter
        config={config}
        filterHideDefaultIsReq
        useOutInput
        // useOutInput 时不依赖后端 selector，避免 NewFilter 一直 isLoading
        outSelector={{ gameCode: 'performance' }}
        pageDefaultSelector={COUNTRY_SELECTOR}
        openSticky={openSticky}
        className={className}
        path={path}
        value={filterValueRef.current}
        inputList={inputList}
        onInit={onInit}
        onConfirm={onConfirm}
        onReset={onReset}
        isHaveFilterSave={false}
      >
        {children}
      </Filter>
    </CountryRegionOptionsContext.Provider>
  );
};

export default PerformanceFilter;
